import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  AnalyticsService,
  ComponentTheme,
  DialogComponent,
  DialogConfig,
  DialogService,
  FlyoutMenu,
  NavigationService,
  ResponsiveService,
} from '@frk/eds-components';
import { WINDOW } from '@ng-web-apis/common';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import { CaveatService } from '@products/caveats/services/caveat.service';
import { FundHeaderComponentConfig } from '@products/fund-header/fund-header-component.config';
import { FundHeaderDisplayComponent } from '@products/fund-header/fund-header-display-component.interface';
import { FavoritesService } from '@products/services/favorites.service';
import { IUserProfile } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { SignInService } from '@services/sign-in.service';
import { StorageService } from '@services/storage.service';
import { TranslateService } from '@shared/translate/translate.service';
import {
  Alert,
  FundShareClassId,
  FundTitleData,
  FundTitleState,
  ProductDetailConfiguration,
  ShareClassData,
} from '@types';
import { ADD_TO_FAV_CLICKED } from '@utils/app.constants';
import { extractFileInfoFromLink } from '@utils/link-utils';
import { Logger } from '@utils/logger';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FundTitleService } from '../services/fund-title.service';
import { SiteConfigService } from '@services/site-config.service';
import { AppStateService } from '@services';

const logger = Logger.getLogger('FundTitleComponent');

@Component({
  selector: 'ft-fund-title',
  templateUrl: './fund-title.component.html',
  styleUrls: ['./fund-title.component.scss'],
})
export class FundTitleComponent
  extends BaseProductDetailComponent
  implements FundHeaderDisplayComponent, OnDestroy, OnInit {
  componentConfiguration: FundHeaderComponentConfig;
  dataSubscription: Subscription;
  fundTitleInfo: FundTitleData = null;
  isFactSheetAvailable = false;
  alerts: Alert[] = [];
  caveatSubscription: Subscription;
  showAlertPopup = false;
  alertConfig: DialogConfig;
  dialogSubscription: Subscription;
  shareClassItems: FlyoutMenu[];
  activeShareClassItems: ShareClassData[];
  downloadLabel: string;
  isHandheld$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  alertWithPopup: Alert;
  hasFavourite = false;
  // showFavourite$: Observable<boolean>;
  hasFavouriteFund$: Observable<boolean>;
  isTemit: boolean;
  tooltipContent: SafeHtml;
  modalLoginSubscription: Subscription;
  isLoggedIn: boolean;
  isLitPreview = false;
  LitPreviewTitle: string;
  // public removeEventListener: () => void;
  private unsubscribe$: Subject<void> = new Subject<void>();

  public fundHeaderProximalValue$: Observable<string[]>;

  public componentTheme: ComponentTheme;

  /**
   * Custom parameters to be used in edsButton directive
   */
  eventParams = {};

  constructor(
    @Inject(WINDOW) readonly windowRef: Window,
    private analytics: AnalyticsService,
    private fundTitleService: FundTitleService,
    private changeDetector: ChangeDetectorRef,
    private navigationService: NavigationService,
    private caveatService: CaveatService,
    private dialogService: DialogService,
    private storageService: StorageService,
    private responsiveService: ResponsiveService,
    private favouriteService: FavoritesService,
    private signInService: SignInService,
    private domSanitizer: DomSanitizer,
    private translateService: TranslateService,
    private profileService: ProfileService,
    private siteConfigService: SiteConfigService,
    private appStateService: AppStateService
  ) {
    super();
    this.isHandheld$ = responsiveService.isHandheld$();
    this.isMobile$ = responsiveService.isMobile$();
    this.hasFavourite = this.favouriteService.isFavouriteEnabled();
    // this.showFavourite$ = this.favouriteService.hasFavorites$();
    this.subscribeToModalLogin();
    this.tooltipContent = this.domSanitizer.bypassSecurityTrustHtml(
      this.translateService.instant('products.sign-in-tooltip-content')
    );
  }

  populate(
    componentConfig: FundHeaderComponentConfig,
    brConfig: ProductDetailConfiguration,
    component,
    page
  ) {
    this.initialize(brConfig, component, page);
    this.componentConfiguration = componentConfig;
    if (this.brConfig.fundId) {
      this.dataSubscription = this.fundTitleService.fundTitleState.subscribe(
        (newState: FundTitleState) => {
          this.mapState(newState);
        }
      );
      this.caveatSubscription = this.caveatService
        .getFundAlerts$(this.brConfig.fundId, this.brConfig.shareClassCode)
        .subscribe((alerts) => {
          this.alerts = alerts;
          this.checkForAlertPopup();
        });

      // WDE-4351: Manually read the value against caveat key and manage its margin bottom from our code.
      this.fundHeaderProximalValue$ = this.caveatService.getProximals$(
        'FundHeaderProximalTop'
      );

      this.fundTitleService.populate(brConfig);
    }
  }

  ngOnInit() {
    this.componentTheme = this.appStateService.getFundHeaderTheme();
    // this.attachClickListener();
    this.favouriteService?.hasFavorites$().subscribe((hasFavorites) => {
      if (hasFavorites) {
        this.favouriteService.loadFavorites();
      }
    });
    this.profileService
      .getUserProfile$()
      ?.pipe(takeUntil(this.unsubscribe$))
      .subscribe((profile: IUserProfile) => {
        this.isLoggedIn = profile?.isLoggedIn;

        // NGC-12290 if a public user clicked on add to favorites
        // and logs in then toggle favorite
        if (this.isLoggedIn) {
          this.storageService
            .retrieve(ADD_TO_FAV_CLICKED, true)
            .then((fundShareClassId) => {
              if (fundShareClassId === this.getFundShareClassId()) {
                this.favouriteService.toggleFavorite(
                  this.getFundShareClassId(),
                  this.fundTitleInfo?.fundName
                );
              }
              this.storageService.remove(ADD_TO_FAV_CLICKED, true);
            });
        }
      });
    this.isLitPreview =
      this.page?.getComponent()?.getParameters()?.pageType === 'lit-preview';
  }

  subscribeToModalLogin() {
    this.modalLoginSubscription = this.favouriteService
      .hideSignModalAfterLogin$()
      .subscribe((resp) => {
        if (resp) {
          this.signInService.hideSignInModal();
        }
      });
  }

  /**
   * Called when state changes.
   * @param newState the new state returned by overview service with fund header response
   */
  mapState(newState: FundTitleState) {
    this.isTemit = newState.isTemit;
    this.fundTitleInfo = newState.data;
    this.getIsFavourite();
    this.changeDetector.detectChanges();
    if (this.fundTitleInfo.downloadLink) {
      this.isFactSheetAvailable = true;
    } else {
      this.isFactSheetAvailable = false;
    }
    this.activeShareClassItems = this.fundTitleInfo?.shareClasses?.filter(
      (data) => data.isActive
    );
    this.shareClassItems = this.activeShareClassItems?.map(
      (shareClass: ShareClassData) => {
        return {
          link: this.siteConfigService.isCanada()
            ? `${shareClass.shareClassName}-${shareClass.shareClassCurrency}`
            : shareClass.shareClassName,
          value: shareClass.shareClassCode,
          href: shareClass.link,
        };
      }
    );
    this.downloadLabel = this.fundTitleInfo?.downloadLabel;
    if (this.fundTitleInfo?.selectedShareClass) {
      this.LitPreviewTitle = `${this.fundTitleInfo.fundName} - ${
        this.fundTitleInfo.selectedShareClass
      } ${this.translateService.instant('products.regulatory-documents')}`;
    } else {
      this.LitPreviewTitle = `${
        this.fundTitleInfo.fundName
      } ${this.translateService.instant('products.regulatory-documents')}`;
    }
  }

  getIsFavourite() {
    if (this.favouriteService?.getFavoriteConfig()?.hasFavorites) {
      this.hasFavouriteFund$ = this.favouriteService.isFavorite$(
        this.getFundShareClassId()
      );
    }
  }

  getFundShareClassId(): FundShareClassId {
    return `${this?.brConfig?.fundId}-${this?.brConfig?.shareClassCode}` as FundShareClassId;
  }

  onShareClassSelection(shareClass: any) {
    if (this.shareClassItems[shareClass]?.href) {
      this.navigationService.navigateByUrl(
        this.shareClassItems[shareClass]?.href
      );
    }
  }

  /**
   * open the target url to download the document in new tab.
   */
  downloadDocument(target: string) {
    this.createEventParams(target);
    this.windowRef.open(target, '_blank');
  }

  /**
   * Parses params and converts them to event object
   */
  createEventParams(target): void {
    const {
      fileName,
      fileExtension,
    }: { fileName: string; fileExtension: string } = extractFileInfoFromLink(
      target
    );

    this.analytics.trackEvent({
      event: 'file_download',
      link_text: 'Download Factsheet',
      detailed_event: 'Download Link Clicked',
      link_url: target,
      file_name: fileName,
      file_extension: fileExtension,
      event_data: {
        file_extension: fileExtension,
        file_lit_code: '',
        file_name: fileName,
        link_url: target,
        location: '',
      },
    });
  }

  checkForAlertPopup() {
    if (this.alerts.length > 0) {
      this.alertWithPopup = this.alerts.find(
        // just show the first popup we find
        (alert: Alert) =>
          alert.popupCloseLabel &&
          alert.popupConfirmLabel &&
          alert.popupContent &&
          alert.popupTitle
      );
      if (this.alertWithPopup) {
        this.storageService
          .retrieve<string>(this.getAlertStorageKey(this.alertWithPopup), true)
          .then((hasConfirmed) => {
            if (!hasConfirmed) {
              this.alertConfig = {
                closeBtnLabel: this.alertWithPopup.popupCloseLabel,
                title: this.alertWithPopup.popupTitle,
                okBtnLabel: this.alertWithPopup.popupConfirmLabel,
                cancelBtnLabel: this.alertWithPopup.popupCloseLabel, // no cancel button in old implementation
              };
              this.dialogSubscription = this.dialogService
                .getDialogClosedObs()
                .subscribe((dialog: DialogComponent) => {
                  // check this is our dialog
                  if (dialog?.dialogId === 'alertPopup') {
                    logger.debug('dialog closed', dialog);
                    // currently only get message when confirm button is hit so just assume confirmed.
                    this.storageService.store<boolean>(
                      this.getAlertStorageKey(this.alertWithPopup),
                      true,
                      true
                    );
                  }
                });
              this.showAlertPopup = true;
            }
          });
      }
    }
  }

  toggleFavourite(addToFavClicked?: boolean): void {
    if (this.isLoggedIn) {
      this.favouriteService.toggleFavorite(
        this.getFundShareClassId(),
        this.fundTitleInfo?.fundName
      );
      // track UA and GA4 event in the single event for add focus list
      this.analytics.trackEvent({
        event: addToFavClicked ? 'add_product_list' : 'remove_product_list',
        detailed_event: addToFavClicked
          ? 'Add Product List'
          : 'Remove Product List',
        fund_name: this.fundTitleInfo.fundName,
        event_data: {
          cart_type: 'focus list',
          fund_class: this.fundTitleInfo?.selectedShareClass,
          fund_name: this.fundTitleInfo.fundName,
          fund_number: Number(this.brConfig.fundId),
          fund_ticker: this.fundTitleInfo.identifier,
        },
      });
    } else {
      // If a public user clicks on Add to favorite
      if (addToFavClicked) {
        this.storageService.store(
          ADD_TO_FAV_CLICKED,
          this.getFundShareClassId(),
          true
        );
      }

      this.openSignInModal();
    }
  }

  openSignInModal(): void {
    return this.signInService.showSignInModal();
  }

  private getAlertStorageKey(alertWithPopup: Alert): string {
    const fsc = `${this.brConfig.fundId}-${this.brConfig.shareClassCode}`;
    if (alertWithPopup.fscl.includes(fsc)) {
      return `alert_${fsc}`;
    }
    return `alert_${this.brConfig.fundId}`;
  }

  // for accessability
  onKeyDown($event: KeyboardEvent) {
    if ($event.code === 'Space') {
      this.toggleFavourite(true);
    }
  }

  ngOnDestroy() {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.unsubscribe();
    this.caveatSubscription?.unsubscribe();
    this.dialogSubscription?.unsubscribe();
    this.modalLoginSubscription?.unsubscribe();
    // this.removeEventListener();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
